import { clamp01, smoothstep } from '../core/math';
import { Ring } from '../core/ring';
import type { PoseFrame } from '../pose/types';
import type { FootworkEvent, FootworkKind } from './events';

/**
 * Footwork from monocular cues.
 *
 * Honest limitation up front: we cannot see feet reliably. A desk webcam crops
 * the legs; a phone on the floor sees them but loses the head. So footwork is
 * inferred from *whole-body* displacement in the ring plane, which is robust
 * precisely because it does not depend on ankles being visible:
 *
 *   lateral (x) — hip-centre image displacement / apparent shoulder width.
 *                 Exact up to the model's hip estimate. No FOV assumption.
 *   depth   (z) — apparent-size change. Assumes a pinhole camera and a fixed
 *                 FOV, so it is directionally right but not metrically exact.
 *                 Treated as a soft cue that biases range, never as truth.
 *   pivot       — torso yaw rate with little translation. This one is genuinely
 *                 well-observed, because shoulder-line foreshortening is a strong
 *                 monocular signal.
 *
 * Practical consequence: step-in/step-out is deliberately given a *higher*
 * threshold than lateral movement, because its underlying signal is noisier and
 * false "you stepped in" readings would wreck range management scoring.
 */

const TUNING = {
  lateralEnter: 0.28, // sw
  depthEnter: 0.4, // sw — deliberately larger; see note above
  minSpeed: 0.7, // sw/s
  pivotRate: 85, // deg/s
  pivotMinSweep: 22, // deg
  refractoryMs: 220,
  windowMs: 500,
};

let nextId = 1;

interface Sample {
  t: number;
  x: number;
  z: number;
  yaw: number;
  sp: number;
}

export class FootworkRecogniser {
  private hist = new Ring<Sample>(60);
  private lastEmit = -1e9;
  /** Where the player has settled — the origin that steps are measured from. */
  private baseX = 0;
  private baseZ = 0;
  private baseYaw = 0;
  private settled = true;
  private settledSince = -1;

  reset(): void {
    this.hist.clear();
    this.settled = true;
    this.settledSince = -1;
  }

  update(f: PoseFrame): FootworkEvent | null {
    const s: Sample = { t: f.t, x: f.ring.x, z: f.ring.z, yaw: f.frame.yaw, sp: Math.hypot(f.ringVel.x, f.ringVel.z) };
    this.hist.push(s);
    if (this.hist.size < 5) {
      this.baseX = s.x;
      this.baseZ = s.z;
      this.baseYaw = s.yaw;
      return null;
    }

    const speed = s.sp;
    const yawRate = this.yawRate();
    // Gate on the PEAK speed over the recent window, not the instantaneous one.
    // A step accelerates, then coasts to a stop — so by the time the displacement
    // is large enough to be a step, the current speed has already decayed below
    // any sensible threshold. Testing instantaneous speed meant footwork was
    // detectable only during the first two frames, when displacement was still
    // tiny, and consequently never fired at all.
    const peakSpeed = this.peakSpeedWithin(f.t, TUNING.windowMs);

    // While moving we do not update the base; once still, the base creeps toward
    // the current position so a player who genuinely relocates does not keep
    // generating "you stepped back" events forever.
    //
    // The creep must be BOTH delayed and slow. An earlier version crept at 5%
    // per frame the instant speed dropped, which tracked the tail of a step
    // faster than the step completed — the baseline swallowed the displacement
    // and no footwork event ever fired.
    if (speed < 0.25 && Math.abs(yawRate) < 30) {
      if (this.settledSince < 0) this.settledSince = f.t;
      this.settled = true;
      if (f.t - this.settledSince > 500) {
        this.baseX += (s.x - this.baseX) * 0.02;
        this.baseZ += (s.z - this.baseZ) * 0.02;
        this.baseYaw += (s.yaw - this.baseYaw) * 0.02;
      }
    } else {
      this.settledSince = -1;
    }

    if (f.t - this.lastEmit < TUNING.refractoryMs) return null;

    const dx = s.x - this.baseX;
    const dz = s.z - this.baseZ;
    const yawSweep = s.yaw - this.baseYaw;

    let kind: FootworkKind | null = null;
    let magnitude = 0;
    let confidence = 0;

    if (Math.abs(yawRate) > TUNING.pivotRate && Math.abs(yawSweep) > TUNING.pivotMinSweep && Math.hypot(dx, dz) < 0.5) {
      kind = yawSweep > 0 ? 'pivot-right' : 'pivot-left';
      magnitude = Math.abs(yawSweep);
      confidence = clamp01(smoothstep(TUNING.pivotMinSweep, 55, Math.abs(yawSweep)));
    } else if (peakSpeed > TUNING.minSpeed && Math.abs(dx) > TUNING.lateralEnter && Math.abs(dx) > Math.abs(dz) * 0.8) {
      kind = dx > 0 ? 'step-right' : 'step-left';
      magnitude = Math.abs(dx);
      confidence = clamp01(smoothstep(TUNING.lateralEnter, 0.6, Math.abs(dx)));
    } else if (
      peakSpeed > TUNING.minSpeed &&
      Math.abs(dz) > TUNING.depthEnter &&
      // A lean-back moves the torso away from the camera and so shrinks the
      // apparent shoulder width — indistinguishable from a step-out to a
      // pure apparent-size depth cue. Replay of a scripted lean produced
      // phantom step-out/step-in pairs. Suppress the depth branch while the
      // spine is tilted; lateral and pivot detection are unaffected.
      Math.abs(f.leanBack) < 0.08
    ) {
      kind = dz > 0 ? 'step-in' : 'step-out';
      magnitude = Math.abs(dz);
      // Depth confidence is capped: we never claim to be sure about monocular z.
      confidence = 0.75 * clamp01(smoothstep(TUNING.depthEnter, 0.9, Math.abs(dz)));
    }

    if (!kind || !this.settled) return null;

    this.settled = false;
    this.settledSince = -1;
    this.lastEmit = f.t;
    this.baseX = s.x;
    this.baseZ = s.z;
    this.baseYaw = s.yaw;

    return {
      id: nextId++,
      kind,
      tStart: f.t - TUNING.windowMs,
      tEnd: f.t,
      magnitude,
      confidence: clamp01(confidence * (0.5 + 0.5 * smoothstep(0.5, 0.9, f.quality))),
    };
  }

  private peakSpeedWithin(now: number, windowMs: number): number {
    let best = 0;
    for (const s of this.hist.recent()) {
      if (now - s.t > windowMs) break;
      if (s.sp > best) best = s.sp;
    }
    return best;
  }

  private yawRate(): number {
    const a = this.hist.at(0);
    const b = this.hist.at(4);
    if (!a || !b) return 0;
    const dt = Math.max(1e-3, (a.t - b.t) / 1000);
    return (a.yaw - b.yaw) / dt;
  }
}

export const FOOTWORK_TUNING = TUNING;
